
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFileExcel, FaFilePdf, FaTimes } from "react-icons/fa";
import { format, subDays } from "date-fns";
import "./CurrentSummaryReport.css";
import { useQuery } from "@tanstack/react-query";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

const useAvailableVehiclesQuery = () => {
  const fetchAvailableVehicles = async () => {
    const token = localStorage.getItem("token");
    if (!token)
      throw new Error("Authentication token not found. Please log in.");

    const response = await fetch(`${API_BASE_URL}/vehicles`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.Error || `HTTP error! status: ${response.status}`,
      );
    }

    const data = await response.json();
    return data.map((v) => v.VehicleID);
  };

  return useQuery({
    queryKey: ["availableVehicles"],
    queryFn: fetchAvailableVehicles,
    staleTime: Infinity,
  });
};

const CurrentSummaryReport = () => {
  const [searchInput, setSearchInput] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [dateRange, setDateRange] = useState("Last Day");
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 1), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);

  const searchInputRef = useRef(null);

  const {
    data: availableVehicles = [],
    isLoading: fetchingVehicles,
    error: vehicleFetchError,
  } = useAvailableVehiclesQuery();

  const [reportData, setReportData] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const hasData = reportData.length > 0;
  const handleGenerateClick = () => {
  setPage(1);
  handleGenerateReport();
  setReportGenerated(true);
};


 useEffect(() => {
  if (reportGenerated) {
    handleGenerateReport();
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [page, reportGenerated]);

  const handleGenerateReport = async () => {
    try {
      setLoadingReport(true);

      const token = localStorage.getItem("token");

      const vehicleIds = selectedVehicles.join(",");

      const response = await fetch(
        `${API_BASE_URL}/todays-summary-report?vehicle_id=${vehicleIds}&start_date=${startDate}&end_date=${endDate}&page=${page}&per_page=${perPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.Error || "Failed to fetch report");
      }

      setReportData(data.data || []);
      setTotalPages(data.pages || 0);
      setTotalRecords(data.total || 0);
    } catch (err) {
      console.error("Current Summary Report Error:", err);
      alert(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownload = async (fileType) => {
    try {
      const token = localStorage.getItem("token");

      const vehicleIds = selectedVehicles.join(",");

      const response = await fetch(
        `${API_BASE_URL}/todays-summary-report?vehicle_id=${vehicleIds}&download=${fileType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.Error || "Download failed");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = url;
      a.download =
        fileType === "xlsx"
          ? "todays_summary_report.xlsx"
          : "todays_summary_report.pdf";

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to download file");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
    setShowSuggestions(true);
  };

  const onSuggestionClick = (vehicleId) => {
    setSearchInput("");
    setShowSuggestions(false);

    if (!selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles([...selectedVehicles, vehicleId]);
    }
  };

  useEffect(() => {
    const filtered =
      searchInput.length > 0
        ? availableVehicles.filter((vehicle) =>
            vehicle.toLowerCase().includes(searchInput.toLowerCase()),
          )
        : availableVehicles;

    setFilteredSuggestions(filtered);
  }, [searchInput, availableVehicles]);

  const removeVehicle = (vehicle) => {
    setSelectedVehicles(selectedVehicles.filter((v) => v !== vehicle));
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);

    const today = new Date();

    const dateMap = {
      "Last Day": 1,
      "Last 7 Days": 7,
      "Last 30 Days": 30,
    };

    if (dateMap[value] !== undefined) {
      setStartDate(format(subDays(today, dateMap[value]), "yyyy-MM-dd"));
      setEndDate(format(today, "yyyy-MM-dd"));
    }
  };

  const handleReset = () => {
    setSearchInput("");
    setSelectedVehicles([]);
    setDateRange("Last Day");
    setStartDate(format(subDays(new Date(), 1), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setReportData([]);
  };

  return (
    <div className="current-summary-report-container">
      <h1 className="report-title">Current Summary Report</h1>
      <hr className="current-summary-report-line" width="100%" />
      <div className="controls-container">
        <div className="search-container-current-summary" ref={searchInputRef}>
          <FaSearch className="search-icon-current-summary" />

          <input
            type="text"
            placeholder="Search Vehicles or Groups"
            className="search-input-current-summary"
            value={searchInput}
            onChange={handleSearchInputChange}
            onFocus={() => {
              setFilteredSuggestions(availableVehicles);
              setShowSuggestions(true);
            }}
          />

          {showSuggestions && filteredSuggestions.length > 0 && (
            <ul className="suggestions-list-alert">
              {filteredSuggestions.map((vehicleId, index) => (
                <li
                  key={index}
                  className="suggestion-item-alert"
                  onClick={() => onSuggestionClick(vehicleId)}
                >
                  {vehicleId}
                </li>
              ))}
            </ul>
          )}

          {fetchingVehicles && (
            <div className="loading-suggestions-alert">Loading vehicles...</div>
          )}

          {vehicleFetchError && (
            <div className="error-message-small-alert">
              {vehicleFetchError.message}
            </div>
          )}
        </div>

        <div className="date-range-container">
          <label className="control-label">Date Range</label>
          <select
            value={dateRange}
            onChange={handleDateRangeChange}
            className="date-range-select"
          >
            <option>Last Day</option>
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Custom</option>
          </select>
        </div>

        <div className="date-input-container">
          <label className="control-label">Start Date:</label>
          <input
            type="date"
            className="date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        <div className="date-input-container">
          <label className="control-label">End Date:</label>
          <input
            type="date"
            className="date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        <button
          className={`generate-report-btn ${
            selectedVehicles.length === 0 ? "disabled-btn" : ""
          }`}
          onClick={handleGenerateClick}
          disabled={selectedVehicles.length === 0 || loadingReport}
        >
          {loadingReport ? "LOADING..." : "GENERATE REPORT"}
        </button>

        <button
          className="action-btn-current-summary reset-btn"
          onClick={handleReset}
        >
          RESET
        </button>
        <Link to="/Report">
          <button className="action-btn-current-summary back-btn">
            BACK TO ALL REPORTS
          </button>
        </Link>
        <FaFileExcel
          className={`excel-icon file-icon ${
            loadingReport || !hasData ? "disabled-icon" : ""
          }`}
          title={hasData ? "Export to Excel" : "No data available to export"}
          onClick={() => hasData && handleDownload("xlsx")}
        />
        <FaFilePdf
          className={`pdf-icon file-icon ${
            loadingReport || !hasData ? "disabled-icon" : ""
          }`}
          title={hasData ? "Export to PDF" : "No data available to export"}
          onClick={() => hasData && handleDownload("pdf")}
        />
      </div>

      <div className="selected-vehicles-container">
        {selectedVehicles.length > 0 ? (
          selectedVehicles.map((vehicle, idx) => (
            <span key={idx} className="vehicle-tag">
              {vehicle}
              <FaTimes
                className="remove-icon"
                onClick={() => removeVehicle(vehicle)}
                title="Remove vehicle"
              />
            </span>
          ))
        ) : (
          <div className="no-vehicles-selected">No vehicles selected</div>
        )}
      </div>
      <div className="current-summary-table-container">
        {reportData.length > 0 && (
          <div className="report-summary-bar">
            Showing <strong>{reportData.length}</strong> of{" "}
            <strong>{totalRecords}</strong> records | Page{" "}
            <strong>{page}</strong> of <strong>{totalPages}</strong>
          </div>
        )}
        <table className="current-summary-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Vehicle No</th>
              <th>Device ID</th>
              <th>Group</th>
              <th>Day Start Time</th>
              <th>Start Address</th>
              <th>Current Address</th>
              <th>Current Speed</th>
              <th>Distance Covered</th>
              <th>Last Tracked Time</th>
              <th>Total Time Running</th>
              <th>Halt Time</th>
              <th>Idle Time</th>
              <th>Utilization</th>
              <th>Efficiency</th>
              <th>Alerts Count</th>
              <th>DTC</th>
            </tr>
          </thead>

          <tbody>
            {loadingReport ? (
              <tr>
                <td colSpan="17">Loading...</td>
              </tr>
            ) : reportData.length > 0 ? (
              reportData.map((row, index) => (
                <tr key={index}>
                  <td>{row.Date}</td>
                  <td>{row["Vehicle number"]}</td>
                  <td>{row["Device ID"]}</td>
                  <td>{row.Group}</td>
                  <td>{row["Day start time"]}</td>
                  <td>{row["Start address"]}</td>
                  <td>{row["Current address"]}</td>
                  <td>{row["Current speed"]}</td>
                  <td>{row["Distance covered"]}</td>
                  <td>{row["Last time tracked"]}</td>
                  <td>{row["Total time Running (mins)"]}</td>
                  <td>{row["Halt Time (mins)"]}</td>
                  <td>{row["Idle Time (mins)"]}</td>
                  <td>{row.Utilization}</td>
                  <td>{row.Efficiency}</td>
                  <td>{row["Alerts count"]}</td>
                  <td>{row["DTC (Active Faults)"]}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="17">No Data Found</td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="pagination-container-alert">
            <button
              className="pagination-btn-alert"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loadingReport}
            >
              ← Prev
            </button>

            <span className="pagination-info-alert">
              Page {page} / {totalPages}
            </span>

            <button
              className="pagination-btn-alert"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loadingReport}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentSummaryReport;
